import moment from "moment";
import { formatNumberForSearch } from "./utils";

// Shared, side-effect-free helpers for the client-side search on the Insurer
// Reports and Bank Statements pages. Kept free of store/React/i18n imports so
// the two filter actions can reuse the same matching semantics and this stays
// directly unit-testable.

// Normalise a raw search term to the form used for matching: lower-cased and
// trimmed. An empty result means "no filter".
export const normalizeSearchTerm = (term: string | null | undefined): string =>
  (term ?? "").toString().toLowerCase().trim();

// Coerce a numeric field to the same locale string the grid displays, so a user
// typing what they see on screen matches. Mirrors the grid's number cells.
export const searchNumberValue = (value: number): string =>
  formatNumberForSearch(value);

// Coerce a date field to its DD.MM.YYYY display form (empty when unset).
export const searchDateValue = (value: string | null | undefined): string =>
  value ? moment(value).format("DD.MM.YYYY") : "";

// True when the term is empty/whitespace, or any candidate contains it as a
// case-insensitive substring. null/undefined candidates count as "". Callers
// coerce numbers/dates to their display form (searchNumberValue/searchDateValue)
// before passing them in.
export const matchesSearchTerm = (
  candidates: Array<string | number | null | undefined>,
  term: string
): boolean => {
  const normalized = normalizeSearchTerm(term);
  if (normalized === "") return true;
  return candidates.some((candidate) =>
    (candidate ?? "").toString().toLowerCase().includes(normalized)
  );
};

export type ParentVisibility = {
  // Whether the parent row should render.
  visible: boolean;
  // Whether the parent has at least one matching child (drives auto-expansion).
  anyChildMatch: boolean;
  // Per-child search-match flag, aligned to the input order.
  childSearchMatches: boolean[];
};

// Decide a parent row's visibility and its children's search-match flags from
// the parent's own match and each child's match. Strict pruning: while a term is
// active only matching children stay flagged, regardless of whether the parent
// matched; an empty term flags every child so visibility falls back to the
// pre-existing selection/toggle logic.
export const resolveParentVisibility = (
  parentMatch: boolean,
  childMatches: boolean[],
  isTermEmpty: boolean
): ParentVisibility => {
  if (isTermEmpty) {
    return {
      visible: true,
      anyChildMatch: false,
      childSearchMatches: childMatches.map(() => true),
    };
  }
  const anyChildMatch = childMatches.some(Boolean);
  return {
    visible: parentMatch || anyChildMatch,
    anyChildMatch,
    childSearchMatches: childMatches.slice(),
  };
};
